import { Box, useTheme } from "@mui/material";
import { Ref, forwardRef, useEffect, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { UIRefreshCategoryEnum } from "../../../common/enums/UIRefreshCategoryEnum";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import NoData from "../../../components/no-data/NoData";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SummaryListingGrid from "../../../components/summary-listing-grid/SummaryListingGrid";
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useProgressContext } from "../../../contexts/progress-context/ProgressContext";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { DEFAULT_REFRESH_SECONDS } from "../../../queries/BaseUseQueryParameters";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useTankBatterySummaryData } from "../../../queries/group-summary/tank-batteries/UseTankBatterySummaryData";
import { actionItems } from "../SummaryListingActions";
import { useSummaryListingContext } from "../SummaryListingContext";
import useTankBatteryViewState from "./MobileTankBatteryViewState";
import { createGridData } from "./MobileTankBatteryViewUtils";

export type MobileTankBatteryViewProps = {
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type MobileTankBatteryViewType = {
    executeAction: (action: BaseActionEnum) => void;
};

function MobileTankBatteryView({
    setViewOptionState
}: MobileTankBatteryViewProps, ref: Ref<unknown> | undefined) {
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const noDataMessage = t("summaryListing.nodata", { objectType: 'tank batteries' });
    const noDataFilterMessage = t("summaryListing.nodataFilter", { objectType: 'tank batteries' });

    const { /*state: summaryListingState,*/ setActions, setTankBatteryCount } = useSummaryListingContext();

    const [progressState] = useProgressContext();    

    const {
        state,
        resetOptions,
        setFilterText,
        setNoData,
        setRefreshing
    } = useTankBatteryViewState();

    useImperativeHandle<unknown, MobileTankBatteryViewType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const { data: summaryData, status, refetch } = useQueryNotifications(useTankBatterySummaryData)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        offline: offlineState.offline,
        enabled: true,
        refreshSeconds: userState.refreshIntervals.find(o => o.category === UIRefreshCategoryEnum.SummaryListing)?.refreshSeconds ?? DEFAULT_REFRESH_SECONDS,
        onSuccess: () => {
            setTimeout(() => setRefreshing(false), 2000);
        },
        onError: () => {
            setRefreshing(false);
        }
    });

    const theme = useTheme();

    const data = createGridData(summaryData, routeContext.groupID as number, theme);

    useEffect(() => {
        const rowCount = summaryData?.rows.length ?? 0;
        setTankBatteryCount(rowCount);
        setNoData(rowCount === 0);
    }, [summaryData, setTankBatteryCount, setNoData]);

    useEffect(() => {

        const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
        setViewOptionState?.(viewOptionState);

    }, [
        state.isOptionsDirty,
        setViewOptionState
    ]);

    // Don't want to do this. This hides the grid when column filters are applied
    // that result in to data. If the grid is hidden, the user can't see the filters
    // anymore.
    // function onFilterChanged(event: FilterChangedEvent) {
    //     setNoData(event.api.getRenderedNodes().length === 0);
    // }

    function executeAction(action: BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.Refresh:
                setRefreshing(true);
                refetch();
                break;
        }
    }

    function onResetOptions() {
        resetOptions();
    }

        // get progress running for current object
        const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

        useEffect(() => {
    
    
                const items = offlineState.offline ? 
                                [] :
                                [...actionItems].filter(o => o.action === BaseActionEnum.Refresh || o.action === BaseActionEnum.Export);

    
                const exportAction = items.find(o => o.action === BaseActionEnum.Export); 
    
                if (exportAction) {
                    exportAction.disabled = (progress && progress.uid === state.downloadUid) || (summaryData?.rows?.length ?? 0) === 0
                }
    
                // if (!_.isEqual(items, summaryListingState.actions)) {
                    setActions(items);
                // }

        }, [
            progress,
            setActions,
            offlineState.offline,
            state.downloadUid,
            summaryData,
            // summaryListingState.actions
        ]);
    
    

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "hidden",
        }}>
            <LoadingZone isLoading={status === "loading" || state.refreshing}>
                <ContentContainer>
                    <ContentSection>

                        <SummaryListingGrid
                            show={state.isNoData === false}
                            gridData={data}
                            filterText={state.filterText}
                            // onFilterChanged={onFilterChanged}
                            getRowId={data => data.data.tankBatteryID.toString()}
                        />

                        {state.isNoData && (status === "success" || status === "error") &&
                            <NoData text={data.rows.length === 0 ? noDataMessage : noDataFilterMessage} />
                        }

                    </ContentSection>

                    <OptionsSideSheet onReset={onResetOptions}>
                        <SideSheetFilterInput
                            label="Find in Tank Battery List"
                            value={state.filterText}
                            onChange={(value) => {
                                setFilterText(value);
                            }}
                        />
                    </OptionsSideSheet>
                </ContentContainer>
            </LoadingZone>
        </Box>
    );
}

export default forwardRef(MobileTankBatteryView);